// src/access.js
// ─────────────────────────────────────────────────────────────
// Who can use what — the ONE place the app decides which screens and
// actions a staff login gets, plus each staff member's personal prefs
// (Practice mode).
//
// Reece (staff "reece") gets everything. Max (staff "max") is limited (Reece,
// 10/1): the standalone Calculator (no Pitch), his own Expenses, the Lender
// Library chatbot (read-only browsing) and a short Settings page (Practice
// mode + his password). He never sees Reece's leads, files, marketing,
// Outbound or New Call. Hiding screens here is only the front door — the real
// boundary is Supabase RLS (supabase/2026-10-01-max-limited-access.sql) and
// the staff checks in api/anthropic-proxy.js + api/borrower/*.
//
// Max's OWN pipeline (built 10/2 — "if practice mode is on, nothing saves, if
// it's off, then he can enter the stuff in for the lead and track stuff") is
// DORMANT: Reece, 10/2, "don't worry about the max stuff for now, I just want
// to get the calculator to a good spot". The code paths stay (App, Pipeline,
// Loan Detail and the calculator all key off `pipeline` + `ownFilesOnly`),
// switched off by MAX_LIMITED below. To turn it back on, point staffAccess at
// MAX_OWN_PIPELINE — and run supabase/2026-10-02-max-own-pipeline.sql first.
//
// Unknown / missing staff (tests, older call sites that never pass staff)
// is treated as Reece, matching App's long-standing `staff||"reece"`.
// Only the literal key "max" is limited.
//
// Prefs live in the signed-in Supabase Auth user's user_metadata
// (supabase.auth.updateUser({data:{loanflow_prefs:{...}}})), so Practice
// mode follows Max across devices. With no Supabase (offline / local dev)
// they fall back to localStorage.
//
// No LO split here (Reece, 10/1: "don't account for the LO split on Max's
// stuff at all"). Max's prefs saved before that may still carry a loSplit;
// normalizePrefs drops it on read, so it is never exposed, and his next save
// writes {practice} alone. Reece's own split is not a pref — it lives in the
// shared settings row (Settings → My LO Commission Split) and is untouched.
// ─────────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react';
import { supabase, isSupabaseReady, staffFromEmail } from './supabase';
import { C } from './theme';

// Every capability key. Reece: all true except settingsLimited (Reece's
// Settings page is the full one) and ownFilesOnly (he sees every file). Max:
// the three tools he uses and the limited Settings page — no pipeline, so no
// files are loaded, polled or saved for his login and his calculator has no
// Pitch.
//   pipeline     — the Pipeline / Loan Detail screens, the calculator's Pitch,
//                  and (App) loading, polling and saving files at all.
//   ownFilesOnly — the pipeline, Loan Detail and the calculator's pitch work
//                  only on files owned by this login; no owner toggle, no
//                  reassigning, no Re-price Radar.
//   portal       — the borrower-portal controls on Loan Detail (access codes,
//                  invite emails, portal logins, viewing borrower uploads
//                  through the server).
//   whiteboard   — the Whiteboard (10/2): the bottom-left note bubble and the
//                  Command Center's Whiteboard panel (src/whiteboard.js).
//                  Reece's alone — Max gets neither (and his login never
//                  reads the whiteboard_notes table).
const FULL = Object.freeze({
  calculator: true,
  expenses: true,
  library: true,
  settings: true,
  settingsLimited: false,
  leads: true,
  pipeline: true,
  ownFilesOnly: false,
  portal: true,
  dashboard: true,
  inquiry: true,
  newCall: true,
  marketing: true,
  outbound: true,
  letters: true,
  editRateSheets: true,
  editMarket: true,
  uploadLibrary: true,
  whiteboard: true,
});

// Max today (10/2): Calculator (standalone, no Pitch), his Expenses, the
// Lender Library and the short Settings page. No pipeline of any kind.
const MAX_LIMITED = Object.freeze({
  calculator: true,
  expenses: true,
  library: true,
  settings: true,
  settingsLimited: true,
  leads: false,
  pipeline: false,
  ownFilesOnly: false,
  portal: false,
  dashboard: false,
  inquiry: false,
  newCall: false,
  marketing: false,
  outbound: false,
  letters: false,
  editRateSheets: false,
  editMarket: false,
  uploadLibrary: false,
  whiteboard: false,
});

// DORMANT — Max's own pipeline (his files only, owner "max"). Not used by
// staffAccess; kept so the feature can be switched back on in one place
// (the tests of those code paths run against it).
export const MAX_OWN_PIPELINE = Object.freeze(Object.assign({}, MAX_LIMITED, {
  pipeline: true,
  ownFilesOnly: true,
}));

export function isLimitedStaff(staff) {
  return String(staff || "").trim().toLowerCase() === "max";
}

// staffAccess(staff) → capability map (frozen; safe to share).
export function staffAccess(staff) {
  return isLimitedStaff(staff) ? MAX_LIMITED : FULL;
}

// ── Prefs ────────────────────────────────────────────────────
export const PREFS_META_KEY = "loanflow_prefs";
const LS_PREFIX = "loanflow_prefs_";
const DEFAULT_PREFS = Object.freeze({ practice: false });

function keyOf(staff) {
  return isLimitedStaff(staff) ? "max" : "reece";
}

// Normalize whatever came out of user_metadata / localStorage. Only the
// known keys survive: an old loSplit (or anything else) is read harmlessly
// and dropped — never exposed, never written back.
export function normalizePrefs(raw) {
  const p = (raw && typeof raw === "object") ? raw : {};
  return { practice: p.practice === true };
}

// Module cache — the sync source for isPractice() and every hook instance.
const cache = { reece: DEFAULT_PREFS, max: DEFAULT_PREFS };
// Which staff keys have had their real prefs read (session metadata,
// a getUser() round trip, or localStorage). Before that, Max is treated
// as in Practice mode by isPracticeOrUnknown() so nothing slips out
// during the first instant after login.
const loaded = { reece: false, max: false };
const listeners = new Set();
let inflight = { reece: null, max: null };
// Bumped by every reset and every save: a read that started before either
// must not land over what came after (a sign-out, a fresh switch flip).
let era = 0;

function emit() {
  listeners.forEach(function (fn) { try { fn(); } catch (e) { /* listener errors never break prefs */ } });
}

function lsRead(k) {
  try {
    const raw = window.localStorage.getItem(LS_PREFIX + k);
    return raw ? normalizePrefs(JSON.parse(raw)) : null;
  } catch (e) { return null; }
}
function lsWrite(k, prefs) {
  try { window.localStorage.setItem(LS_PREFIX + k, JSON.stringify(prefs)); } catch (e) { /* private mode etc. */ }
}

// Practice mode only exists for Max — Reece's work always saves.
function clampFor(k, prefs) {
  return k === "max" ? prefs : Object.assign({}, prefs, { practice: false });
}

function setCache(k, prefs, markLoaded) {
  cache[k] = Object.freeze(clampFor(k, normalizePrefs(prefs)));
  if (markLoaded) loaded[k] = true;
  emit();
}

// Only the signed-in person's own prefs: a user that belongs to the OTHER
// login (e.g. read while the app still defaults to "reece") never lends its
// metadata to this key.
function ownPrefs(k, user) {
  if (!user) return null;
  const owner = typeof staffFromEmail === "function" ? staffFromEmail(user.email) : null;
  if (owner && owner !== k) return null;
  return user.user_metadata ? user.user_metadata[PREFS_META_KEY] : null;
}

// Seed the cache straight from a Supabase user object (App calls this at
// sign-in so prefs are right before first paint). `provisional` (session
// restore — the stored session can carry metadata another device has since
// changed): show them, but keep the key UNREAD so a limited login still
// writes nothing until the fresh read lands.
export function primeStaffPrefs(staff, user, provisional) {
  const k = keyOf(staff);
  setCache(k, ownPrefs(k, user), !provisional);
  if (!provisional) landed(k);
}

// A fresh read that fails (network blip, laptop waking, a 5xx, the fetch
// timeout, a failed token refresh — supabase-js RESOLVES those with
// {user:null, error}) must never fall back to "Practice off":
//   1. the session this browser already holds (a local read, no network)
//      supplies the prefs — the same metadata a sign-in primes from;
//   2. with no session either, the key stays UNREAD, so isPracticeOrUnknown()
//      keeps every limited-login write blocked.
// Either way the fresh read is retried a few times in the background.
const RETRY_MS = [4000, 15000, 45000, 120000];
let retryTimer = { reece: null, max: null };
let retryCount = { reece: 0, max: 0 };
// A Practice-ON flip made while the key was still unread: applied (and
// saved, merged onto the real stored prefs) as soon as they are read, so a
// late read can't switch Practice back off underneath it.
let pendingOn = { reece: false, max: false };

function scheduleRetry(k) {
  if (retryTimer[k] || retryCount[k] >= RETRY_MS.length) return;
  const myEra = era;
  const ms = RETRY_MS[retryCount[k]++];
  retryTimer[k] = setTimeout(function () {
    retryTimer[k] = null;
    if (myEra === era) loadStaffPrefs(k, true);
  }, ms);
}

function sessionUser() {
  return Promise.resolve()
    .then(function () { return supabase.auth.getSession(); })
    .then(function (res) { return (res && res.data && res.data.session && res.data.session.user) || null; })
    .catch(function () { return null; });
}

// Real prefs are in: drop the retry state and apply a pending Practice-ON.
function landed(k) {
  retryCount[k] = 0;
  if (retryTimer[k]) { clearTimeout(retryTimer[k]); retryTimer[k] = null; }
  if (pendingOn[k]) {
    pendingOn[k] = false;
    if (!cache[k].practice) saveStaffPrefs(k, { practice: true });
  }
}

// Load prefs once per staff key (re-run with force after a sign-in).
export function loadStaffPrefs(staff, force) {
  const k = keyOf(staff);
  if (!force && (loaded[k] || inflight[k])) return inflight[k] || Promise.resolve(cache[k]);
  if (!isSupabaseReady()) {
    setCache(k, lsRead(k), true);
    landed(k);
    return Promise.resolve(cache[k]);
  }
  const myEra = era;
  const fallback = function () {
    return sessionUser().then(function (user) {
      if (myEra !== era) return cache[k];
      if (user) { setCache(k, ownPrefs(k, user), true); landed(k); }
      // else: stays unread — writes blocked, never defaults.
      scheduleRetry(k);
      return cache[k];
    });
  };
  const p = Promise.resolve()
    .then(function () { return supabase.auth.getUser(); })
    .then(function (res) {
      if (myEra !== era) return cache[k];
      const user = res && res.data && res.data.user;
      if ((res && res.error) || !user) return fallback();
      setCache(k, ownPrefs(k, user), true);
      landed(k);
      return cache[k];
    }, function () {
      return myEra === era ? fallback() : cache[k];
    })
    .catch(function () { return cache[k]; })
    .finally(function () { if (inflight[k] === p) inflight[k] = null; });
  inflight[k] = p;
  return p;
}

const UNREAD_ERROR = "your saved settings couldn't be read yet — check your connection";

// A save while the key is still unread. Never merged onto the defaults
// (that would write practice:false over a saved ON). Turning Practice ON
// still takes effect here, in memory — the safe side — and is saved once the
// real prefs are read; anything else waits for a successful read.
function unreadSave(k, patch) {
  const prev = cache[k];
  const want = clampFor(k, normalizePrefs(Object.assign({}, prev, typeof patch === "function" ? patch(prev) : patch)));
  if (want.practice && !prev.practice) {
    pendingOn[k] = true;
    setCache(k, Object.assign({}, prev, { practice: true }), false);
  }
  scheduleRetry(k);
  return Promise.resolve({ ok: false, error: UNREAD_ERROR });
}

// Save prefs. Optimistic: the cache flips immediately (so Practice mode
// takes effect at once), then persists. Resolves to {ok, error}.
export function saveStaffPrefs(staff, patch) {
  const k = keyOf(staff);
  if (!loaded[k]) {
    // Read what is stored first, so the write merges onto it.
    if (!isSupabaseReady()) loadStaffPrefs(k);
    else {
      return loadStaffPrefs(k).then(function () {
        return loaded[k] ? saveStaffPrefs(k, patch) : unreadSave(k, patch);
      });
    }
  }
  const prev = cache[k];
  const next = clampFor(k, normalizePrefs(Object.assign({}, prev, typeof patch === "function" ? patch(prev) : patch)));
  const myEra = ++era;
  setCache(k, next, true);
  if (!isSupabaseReady()) {
    lsWrite(k, next);
    return Promise.resolve({ ok: true });
  }
  return Promise.resolve()
    .then(function () { return supabase.auth.updateUser({ data: { [PREFS_META_KEY]: next } }); })
    .then(function (res) {
      if (res && res.error) throw res.error;
      return { ok: true };
    })
    .catch(function (e) {
      // Turning Practice ON must never be undone by a failed save — the
      // safe direction is "nothing saves". Anything else rolls back.
      if (myEra === era && !(next.practice && !prev.practice)) setCache(k, prev, true);
      return { ok: false, error: (e && e.message) || String(e) };
    });
}

// Forget everything (sign-out), so the next login reads fresh prefs.
export function resetStaffPrefs() {
  era++;
  cache.reece = DEFAULT_PREFS; cache.max = DEFAULT_PREFS;
  loaded.reece = false; loaded.max = false;
  inflight = { reece: null, max: null };
  Object.keys(retryTimer).forEach(function (k) { if (retryTimer[k]) clearTimeout(retryTimer[k]); });
  retryTimer = { reece: null, max: null };
  retryCount = { reece: 0, max: 0 };
  pendingOn = { reece: false, max: false };
  emit();
}

// Sync getters for non-React code (autosave, libraryChat, scenario logging).
export function getStaffPrefs(staff) { return cache[keyOf(staff)]; }
export function isPractice(staff) {
  const k = keyOf(staff);
  return k === "max" && cache.max.practice === true;
}
// Write gates use this: Max whose prefs haven't been read yet counts as
// practice (nothing saves) until they are.
export function isPracticeOrUnknown(staff) {
  const k = keyOf(staff);
  if (k !== "max") return false;
  return !loaded.max || cache.max.practice === true;
}
export function staffPrefsLoaded(staff) { return loaded[keyOf(staff)]; }

export function subscribeStaffPrefs(fn) {
  listeners.add(fn);
  return function () { listeners.delete(fn); };
}

// useStaffPrefs(staff) → [prefs, setPrefs]
//   prefs    {practice:boolean}
//   setPrefs(patchObject | prev=>patch) → Promise<{ok, error?}>
export function useStaffPrefs(staff) {
  const k = keyOf(staff);
  const [prefs, setLocal] = useState(cache[k]);
  useEffect(function () {
    setLocal(cache[k]);
    const off = subscribeStaffPrefs(function () { setLocal(cache[k]); });
    loadStaffPrefs(k);
    return off;
  }, [k]);
  const setPrefs = function (patch) { return saveStaffPrefs(k, patch); };
  return [prefs, setPrefs];
}

// Hook form of isPractice — re-renders when the switch flips.
export function usePractice(staff) {
  const [prefs] = useStaffPrefs(staff);
  return isLimitedStaff(staff) && prefs.practice === true;
}

// ── Practice banner ──────────────────────────────────────────
// Shown on every screen while Practice mode is on (App renders it above the
// view; a screen can add its own line with `detail`). role="status" so a
// screen reader hears it once, not on every render.
export const PRACTICE_TITLE = "Practice mode — nothing is being saved";
export function PracticeBanner({ detail, compact, style }) {
  return React.createElement("div", {
    role: "status",
    "data-practice-banner": "",
    style: Object.assign({
      display: "flex", alignItems: "flex-start", gap: 10,
      padding: compact ? "8px 12px" : "11px 16px",
      marginBottom: compact ? 10 : 16,
      borderRadius: 10,
      background: C.orangeBg,
      border: "1px solid " + C.orange + "55",
      color: C.text,
      fontFamily: "'Mulish',system-ui,sans-serif",
    }, style || {}),
  },
    React.createElement("span", { "aria-hidden": "true", style: { fontSize: compact ? 13 : 15, lineHeight: 1.3 } }, "🧪"),
    React.createElement("div", { style: { minWidth: 0 } },
      React.createElement("div", { style: { fontSize: compact ? 12 : 13, fontWeight: 800, color: C.orange } }, PRACTICE_TITLE),
      React.createElement("div", { style: { fontSize: compact ? 11 : 12, color: C.textMuted, marginTop: 2, lineHeight: 1.45 } },
        detail || "Calculator runs, expense edits and library chats stay on this screen only and are gone when Practice mode is turned off or you sign out. Turn it off in Settings.")
    )
  );
}
